import {
  Button,
  CreateButton,
  DatePicker,
  DeleteButton,
  EditButton,
  ExportButton,
  Icons,
  Image,
  List,
  Modal,
  Radio,
  ShowButton,
  Space,
  Table,
  TableProps,
  Tag,
  notification,
} from '@pankod/refine-antd';
import { CanAccess, IResourceComponentsProps, useNavigation } from '@pankod/refine-core';
import defaultAvatar from 'assets/images/default-avatar.jpg';
import { SearchNew } from 'components';
import CustomTableWithPagination from 'components/table/custom-table';
import dayjs from 'dayjs';
import { IClient, PaginationHandler } from 'interfaces';
import React, { useCallback, useEffect, useState } from 'react';
import { RiArticleLine } from 'react-icons/ri';
import { getAllClient, getClientsExport } from 'services/client';
import { SearchIndex } from 'services/meilisearch';
import { getRestaurantPosInvoice } from 'services/sale';
import { getActivityHistoryUrl } from 'utilities/activity-log';
import { downloadCSVFile } from 'utilities/file';
import MetaData from 'utilities/metadata';
import { getAddressAsString, previewPdfFromBase64 } from 'utilities/shared';

export const ClientList: React.FC<IResourceComponentsProps> = () => {
  const [clients, setClients] = useState<IClient[]>([]);
  const [totalClients, setTotalClients] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [isExportButtonLoading, setIsExportButtonLoading] = useState<boolean>(false);
  const [isInvoiceModalVisible, setIsInvoiceModalVisible] = useState<boolean>(false);
  const [clientIdToPrintInvoice, setClientIdToPrintInvoice] = useState<string | null>(null);

  const { push } = useNavigation();

  const fetchDataOnMount = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getAllClient({
        searchString: searchText,
        start: page,
        limit,
      });
      const clientsData = data.data;
      setClients(clientsData);
      setTotalClients(data.total);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsLoading(false);
  }, [limit, page, searchText]);

  useEffect(() => {
    fetchDataOnMount();
  }, [fetchDataOnMount]);

  const onSelectOption = async (searchText: string) => {
    setSearchText(searchText);
    setPage(1);
  };

  const onDeleteSuccessHandler = async () => {
    try {
      const data = await getAllClient();
      const clientsData = data.data;
      setClients(clientsData);
      setTotalClients(data.total);
    } catch (err) {
      return notification.error({
        message: 'Failed',
        description: 'Failed to delete.',
      });
    }
  };

  const onExportButtonClick = async () => {
    setIsExportButtonLoading(true);
    try {
      const clientsData = await getClientsExport();

      const rows = [['Unique ID', 'Name', 'Email', 'Phone', 'Tags', 'Address']];
      clientsData.forEach((element, i) => {
        const tags = element.clientTags.map((tag) => {
          return tag.tag.name;
        });
        rows.push([
          element.clientCode,
          element.name,
          element.email,
          element.mobileNumber,
          JSON.stringify(tags.join(', ')),
          JSON.stringify(getAddressAsString(element.address)),
        ]);
      });
      downloadCSVFile(rows);
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to Export CSV File.',
      });
    }
    setIsExportButtonLoading(false);
  };

  const pageHeader = (
    <Space wrap size={[8, 8]} style={{ width: '100%' }}>
      <CreateButton type="primary"></CreateButton>
      <Button size="middle" icon={<Icons.ImportOutlined />} onClick={() => push('/clients/import')}>
        Import
      </Button>
      <ExportButton loading={isExportButtonLoading} onClick={onExportButtonClick}></ExportButton>
      <CanAccess resource="activity-history" action="list">
        <Button
          icon={<Icons.HistoryOutlined />}
          onClick={() => push(getActivityHistoryUrl('client'))}
        >
          Activity
        </Button>
      </CanAccess>
    </Space>
  );

  const onTableChange: TableProps<IClient>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra
  ) => {
    setPage(pagination.current ?? 1);
    setLimit(pagination.pageSize ?? 10);
  };

  const onPageChange: PaginationHandler = async (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  return (
    <List headerProps={{ extra: pageHeader }}>
      <MetaData />
      {clientIdToPrintInvoice && (
        <ClientInvoicePrintModal
          visible={isInvoiceModalVisible}
          clientId={clientIdToPrintInvoice}
          onClose={() => {
            setIsInvoiceModalVisible(false);
            setClientIdToPrintInvoice(null);
          }}
        />
      )}

      <SearchNew
        placeholder="Search by Client Code, Name, Email, Mobile Number, Tags"
        size="large"
        className="search"
        index={SearchIndex.Client}
        groups={['clientCode', 'name', 'email', 'mobileNumber', 'tag']}
        onSelectOption={onSelectOption}
        onReset={fetchDataOnMount}
      />
      <CustomTableWithPagination
        total={totalClients}
        page={page}
        limit={limit}
        onPageChange={onPageChange}
      >
        <Table
          dataSource={clients}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: true }}
          onChange={onTableChange}
          // pagination={{
          //   defaultPageSize: 10,
          //   showSizeChanger: true,
          //   total: totalClients,
          //   current: page,
          // }}
          pagination={false}
        >
          <Table.Column<IClient> dataIndex="clientCode" title="Unique ID" />
          <Table.Column<IClient>
            dataIndex="avatar"
            title="Logo"
            width="6%"
            render={(_text, record): React.ReactNode => {
              const clientPhoto = record?.clientPhoto;
              const avatarURL = clientPhoto[clientPhoto?.length - 1]?.avatar?.thumbUrl;

              return (
                <Image
                  src={avatarURL || defaultAvatar}
                  style={{ objectFit: 'cover' }}
                  height={45}
                  width={45}
                  alt={record.name}
                />
              );
            }}
          />
          <Table.Column<IClient> dataIndex="name" title="Name" />
          <Table.Column<IClient> dataIndex="email" title="Email" />
          <Table.Column<IClient> dataIndex="mobileNumber" title="Phone" />
          <Table.Column<IClient>
            dataIndex="clientTags"
            title="Tag"
            render={(_, record) => {
              const clientTags = record?.clientTags || [];
              const tags = clientTags.map((tagObj) => (
                <Tag key={tagObj.tag.id} color="default">
                  {tagObj.tag.name}
                </Tag>
              ));

              return tags;
            }}
          />

          <Table.Column<IClient>
            title="Actions"
            dataIndex="actions"
            width={120}
            render={(_, record): React.ReactNode => {
              return (
                <Space>
                  <ShowButton size="small" recordItemId={record.id} hideText />
                  <EditButton size="small" recordItemId={record.id} hideText />
                  <DeleteButton
                    size="small"
                    hideText
                    recordItemId={record.id}
                    onSuccess={onDeleteSuccessHandler}
                  />
                  <Button
                    size="small"
                    icon={<RiArticleLine size={16} style={{ marginTop: '3px' }} />}
                    onClick={() => {
                      setIsInvoiceModalVisible(true);
                      setClientIdToPrintInvoice(record.id);
                    }}
                  />
                  {/* <InvoiceButton clientId={record.id} /> */}
                </Space>
              );
            }}
          />
        </Table>
      </CustomTableWithPagination>
    </List>
  );
};

type ClientInvoicePrintModalProps = {
  visible: boolean;
  clientId: string;
  onClose: () => void;
};

const ClientInvoicePrintModal: React.FC<ClientInvoicePrintModalProps> = ({
  visible,
  clientId,
  onClose,
}) => {
  const [range, setRange] = useState<string | null>('today');
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleRangeChange = (value: string | null) => {
    setRange(value);

    const today = dayjs();
    switch (value) {
      case 'today':
        setStartDate(today.startOf('day'));
        setEndDate(today.endOf('day'));
        break;
      case 'thisWeek':
        setStartDate(today.startOf('week'));
        setEndDate(today.endOf('week'));
        break;
      case 'thisMonth':
        setStartDate(today.startOf('month'));
        setEndDate(today.endOf('month'));
        break;
      case 'lastMonth':
        setStartDate(today.subtract(1, 'month').startOf('month'));
        setEndDate(today.subtract(1, 'month').endOf('month'));
        break;
      case 'thisYear':
        setStartDate(today.startOf('year'));
        setEndDate(today.endOf('year'));
        break;
      case 'all':
        setStartDate(dayjs('1995-12-04'));
        setEndDate(today);
        break;
      default:
        setStartDate(null);
        setEndDate(null);
    }
  };

  useEffect(() => {
    if (!visible) {
      setRange('today');
    }
  }, [visible]);

  useEffect(() => {
    if (range) {
      handleRangeChange(range);
    }
  }, [range]);

  const handelPrint = async () => {
    if (!startDate || !endDate) {
      setErrorMessage('Please select both from and to dates.');
      return;
    }

    setErrorMessage(null); // clear error when valid

    const from = dayjs(startDate).startOf('day');
    const to = dayjs(endDate).endOf('day');

    try {
      setIsLoading(true);
      const invoiceString = await getRestaurantPosInvoice(
        clientId,
        from.format('YYYY-MM-DD HH:mm:ss'),
        to.format('YYYY-MM-DD HH:mm:ss')
      );
      previewPdfFromBase64(invoiceString);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err?.message,
      });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  return (
    <Modal
      title="Due Bills"
      visible={visible}
      onCancel={onClose}
      width={620}
      closable={false}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="print"
          type="primary"
          loading={isLoading}
          disabled={isLoading}
          onClick={handelPrint}
        >
          Print
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Radio.Group
          value={range}
          onChange={(e) => setRange(e.target.value)}
          style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 8 }}
        >
          <Radio.Button value="today">Today</Radio.Button>
          <Radio.Button value="thisWeek">This Week</Radio.Button>
          <Radio.Button value="thisMonth">This Month</Radio.Button>
          <Radio.Button value="lastMonth">Last Month</Radio.Button>
          <Radio.Button value="thisYear">This Year</Radio.Button>
          <Radio.Button value="all">All</Radio.Button>
        </Radio.Group>

        <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 4, fontWeight: 600, paddingLeft: '5px' }}>From</div>
            <DatePicker
              placeholder="Start Date"
              value={startDate}
              format={'DD-MM-YYYY'}
              onChange={(date) => {
                setStartDate(date);
                setRange(null);
              }}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 4, fontWeight: 600, paddingLeft: '5px' }}>To</div>
            <DatePicker
              placeholder="End Date"
              value={endDate}
              format={'DD-MM-YYYY'}
              onChange={(date) => {
                setEndDate(date);
                setRange(null);
              }}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </Space>

      {errorMessage && (
        <div style={{ color: 'red', textAlign: 'center', marginTop: 12 }}>{errorMessage}</div>
      )}
    </Modal>
  );
};

// type InvoiceButtonProps = {
//   clientId: string;
// };

// const InvoiceButton: React.FC<InvoiceButtonProps> = (props) => {
//   const [isLoading, setIsLoading] = useState<boolean>(false);

//   const handleInvoice = async (id: string) => {
//     try {
//       setIsLoading(true);
//       const invoiceString = await getRestaurantPosInvoice(props.clientId);
//       print(invoiceString);
//     } catch (err: any) {
//       notification.error({
//         message: 'Failed',
//         description: err?.message,
//       });
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   return (
//     <Button
//       size="small"
//       loading={isLoading}
//       icon={<RiArticleLine size={16} style={{ marginTop: '3px' }} />}
//       onClick={() => handleInvoice(props.clientId)}
//     />
//   );
// };
